import React, { useEffect, useState } from "react";
import GenericList from "../shared/components/GenericList";
import { GridColDef } from "@mui/x-data-grid";
import { bokimbolaUrl, questionnaireUrl } from "../../configs/URL"; // tu peux remplacer par ton vrai endpoint
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from "recharts";
import { Grid2 } from "@mui/material";

export interface Bokimbola {
  id: number;
  Caisse_ny: string;
  Laharana: string;
  Fanoritsoritana: string;
  Kaonty_budget: string;
  Laharana_Faktiora: string;
  Vola_miditra: number;
  vola_mivoaka: number;
  Solde?: number; // calculé côté front
}

export const BokimbolaTable: React.FC = () => {
  const [data, setData] = useState<Bokimbola[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch(bokimbolaUrl); // remplace ici par ton URL Bokimbola
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        const mapped: Bokimbola[] = json.results.map((r: any, index: number) => {
          return {
            id: r._id || index,
            Caisse_ny: r.Caisse_ny,
            Laharana: r.Laharana,
            Fanoritsoritana: r.Fanoritsoritana,
            Kaonty_budget: r.Kaonty_budget,
            Laharana_Faktiora: r.Laharana_Faktiora,
            Vola_miditra: Number(r.Vola_miditra) || 0,
            vola_mivoaka: Number(r.vola_mivoaka) || 0,
          };
        });

        // calcul du solde cumulé
        let solde = 0;
        const withSolde = mapped.map((x) => {
          solde += x.Vola_miditra - x.vola_mivoaka;
          return { ...x, Solde: solde };
        });

        setData(withSolde);
      } catch (err: any) {
        setError(err.message || "Erreur de chargement");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <div>Chargement…</div>;
  if (error) return <div style={{ color: "red" }}>Erreur: {error}</div>;

  const BokimbolaFields: GridColDef[] = [
    { field: "Caisse_ny", headerName: "Caisse", width: 150 },
    { field: "Laharana", headerName: "Laharana (Référence)", width: 150 },
    { field: "Fanoritsoritana", headerName: "Fanoritsoritana (Description)", width: 250 },
    { field: "Kaonty_budget", headerName: "Kaonty Budget", width: 150 },
    { field: "Laharana_Faktiora", headerName: "Facture N°", width: 150 },
    { field: "Vola_miditra", headerName: "Vola miditra (Entrée)", width: 150, type: "number" },
    { field: "vola_mivoaka", headerName: "Vola mivoaka (Sortie)", width: 150, type: "number" },
    { field: "Solde", headerName: "Solde cumulé", width: 150, type: "number" },
  ];

  return (
    <div style={{ padding: "16px" }}>
      <Grid2 container spacing={2} style={{ marginBottom: "16px" }}>
        <Grid2 size={6}>
          <h3 style={{ textAlign: "center", marginBottom: 10 }}>
            Vola miditra vs vola mivoaka
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Laharana" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Vola_miditra" name="Entrée" fill="#82ca9d" />
              <Bar dataKey="vola_mivoaka" name="Sortie" fill="#ff6666" />
            </BarChart>
          </ResponsiveContainer>
        </Grid2>

        <Grid2 size={6}>
          <h3 style={{ textAlign: "center", marginBottom: 10 }}>
            Évolution du solde cumulé
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Laharana" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="Solde" stroke="#8884d8" name="Solde cumulé" />
            </LineChart>
          </ResponsiveContainer>
        </Grid2>
      </Grid2>

      <GenericList columns={BokimbolaFields} rows={data} />
    </div>
  );
};

export default BokimbolaTable;
